import { Column } from "../../../../../core/components/Column";
import { TextComponent } from "../../../../../core/components/TextComponent";
import { CustomButton } from "../../../../../core/components/CustomButton";
import { useNavigate } from "react-router-dom";
import { useState } from "react";
import { setUserInfo } from "../../store/OnboardingSlice";
import { useDispatch, useSelector } from "react-redux";
import {
	setCurrentRealCoordinates,
	setProfileLocationSource,
	ProfileLocationSource,
} from "../../../../../core/utilities/locationStorage";
import { AppDispatch, RootState } from "../../../../../core/store/store";
import { CustomInput } from "../../../../../core/components/CustomInput";
import { onGetDetailPlaces, onGetPlaces } from "../../../../settings/presentation/store/SettingsThunk";

export const Location = () => {
    const navigate = useNavigate();
    const dispatch: AppDispatch = useDispatch();
    const { search } = useSelector((state: RootState) => state.settings);
    const [isLoading, setIsLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [showManualSearch, setShowManualSearch] = useState(false);
    const [manualSearch, setManualSearch] = useState("");

    const saveLocation = (latitude: number, longitude: number, source: ProfileLocationSource) => {
        const lat = latitude.toString();
        const lng = longitude.toString();
        dispatch(setUserInfo({ lat, lng, country_lat: lat, country_lng: lng, profile_location_source: source }));
        setCurrentRealCoordinates(lat, lng);
        setProfileLocationSource(source);
        setIsLoading(false);
        navigate("/profilepicture");
    };

    const handleAllowLocation = () => {
        setIsLoading(true);
        setErrorMessage("");
        navigator.geolocation.getCurrentPosition(
            (position) => {
                const { latitude, longitude } = position.coords;
                localStorage.setItem("LOCATION_PERMISSION_STATE", "granted");
                saveLocation(latitude, longitude, "browser_location");
            },
            () => {
                localStorage.setItem("LOCATION_PERMISSION_STATE", "denied");
                setErrorMessage("We couldn't read your current location. You can search for your suburb or city instead.");
                setShowManualSearch(true);
                setIsLoading(false);
            }
        );
    };

    const handleManualSearch = async () => {
        const query = manualSearch.trim();
        if (!query) return;
        setErrorMessage("");
        setIsLoading(true);
        await dispatch(onGetPlaces({ search: query }));
        setIsLoading(false);
    };

    const handleManualSelection = async (placeId?: string) => {
        if (!placeId) return;
        setErrorMessage("");
        setIsLoading(true);
        const result = await dispatch(onGetDetailPlaces({ place_id: placeId }));
        const details = result.payload as { location?: { lat?: number; lng?: number } } | undefined;
        const latitude = details?.location?.lat;
        const longitude = details?.location?.lng;
        if (latitude === undefined || longitude === undefined) {
            setErrorMessage("We couldn't use that location. Please choose another result.");
            setIsLoading(false);
            return;
        }
        localStorage.setItem("LOCATION_PERMISSION_STATE", "manual");
        saveLocation(latitude, longitude, "manual_selection");
    };

    return (
        <Column
            crossAxisAlignment="center"
            className="md:bg-white px-10 pb-10 md:my-10 md:p-20 self-center text-center w-full md:w-[584px] lg:shadow-xl rounded-lg"
        >
            <TextComponent variant="h2" className="font-bold">
                Choose your location
            </TextComponent>
            <TextComponent
                variant="h6"
                className="text-[#6B6B6B] mt-2 md:w-[306px]"
            >
                We use your chosen location to show nearby profiles. Choose a town manually, or let your browser provide your location. Device location access is optional.
            </TextComponent>
            <img
                src="/location.svg"
                className="py-10"
                alt="location"
                width={208}
                height={198}
            />
            {errorMessage && (
                <TextComponent variant="h6" className="mb-4 text-red-500">
                    {errorMessage}
                </TextComponent>
            )}
            <div className="grid w-full gap-3">
                <CustomButton color="white" onClick={() => setShowManualSearch(true)} disabled={isLoading}>
                    Choose a town manually
                </CustomButton>
                <CustomButton color="white" onClick={handleAllowLocation} loading={isLoading}>
                    Use my current location
                </CustomButton>
            </div>
            {showManualSearch && (
                <div className="mt-5 w-full text-left">
                    <CustomInput
                        label="Suburb or city"
                        value={manualSearch}
                        hintText="Start typing your location"
                        onChange={(event) => setManualSearch(event.target.value)}
                    />
                    <CustomButton
                        className="mt-3"
                        onClick={handleManualSearch}
                        loading={isLoading}
                        disabled={!manualSearch.trim()}
                    >
                        Search
                    </CustomButton>
                    {search.length > 0 && (
                        <div className="mt-3 rounded-lg border border-gray-300 bg-white">
                            {search.map((place) => (
                                <button
                                    key={place.place_id}
                                    type="button"
                                    className="block w-full border-b border-gray-200 px-3 py-3 text-left last:border-b-0 hover:bg-gray-100"
                                    onClick={() => handleManualSelection(place.place_id)}
                                >
                                    {place.description}
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </Column>
    );
};
